/**
 * Analytics Service
 *
 * Google Analytics 4 (GA4) integration for web platform only.
 * Tracks page views, sessions, and users.
 *
 * IMPORTANT: Only initializes on web platform. Native platforms
 * (Android/iOS) are skipped entirely.
 */

import ReactGA from 'react-ga4';
import { Capacitor } from '@capacitor/core';
import { debugLog, debugError, debugWarn, debugTable, debugGroup, debugGroupEnd } from '../utils/debugUtils';

class AnalyticsService {
  constructor() {
    this.initialized = false;
    this.platform = Capacitor.getPlatform();
    this.measurementId = process.env.REACT_APP_GA4_MEASUREMENT_ID;
  }

  /**
   * Initialize Google Analytics 4
   * Only runs on web platform
   */
  initialize() {
    // Skip if not web platform
    if (this.platform !== 'web') {
      debugLog('[AnalyticsService] Skipping initialization - not web platform');
      return;
    }

    // Skip if no measurement ID configured
    if (!this.measurementId || this.measurementId.trim() === '') {
      console.warn('[AnalyticsService] No measurement ID configured - analytics disabled');
      return;
    }

    try {
      // Initialize react-ga4
      ReactGA.initialize(this.measurementId, {
        gaOptions: {
          // Anonymize IP addresses for GDPR compliance
          anonymize_ip: true,
        },
        gtagOptions: {
          // Custom dimensions/parameters
          platform: 'web',
          app_version: process.env.REACT_APP_VERSION || 'unknown',
        }
      });

      this.initialized = true;
      debugLog('[AnalyticsService] Initialized successfully');
    } catch (error) {
      console.error('[AnalyticsService] Failed to initialize:', error);
    }
  }

  /**
   * Track a page view
   * @param {string} path - Page path (e.g., '/home', '/#/ronda')
   * @param {string} title - Page title
   */
  trackPageView(path, title) {
    if (!this.initialized) {
      return;
    }

    try {
      ReactGA.send({
        hitType: 'pageview',
        page: path,
        title: title || document.title
      });

      debugLog('[AnalyticsService] Page view tracked:', path);
    } catch (error) {
      console.error('[AnalyticsService] Failed to track page view:', error);
    }
  }

  /**
   * Check if analytics is initialized and ready
   * @returns {boolean}
   */
  isReady() {
    return this.initialized;
  }

  /**
   * Get current platform
   * @returns {string}
   */
  getPlatform() {
    return this.platform;
  }
}

// Export singleton instance
export const analyticsService = new AnalyticsService();
export default analyticsService;
